import { useEffect, useState } from 'react';
import { getVenueCoverImage } from 'utils/shared/venues';
import { useModal } from 'hooks/useModal';
import CloseIcon from 'svg/CloseIcon';
import GalleryLeft from 'svg/GalleryLeft';
import GalleryRight from 'svg/GalleryRight';
import MoreGallery from 'svg/MoreGallery';
import { Button } from 'components/Button';
import type { ClubProfileImage } from '../../types';

interface Props {
  images: ClubProfileImage[];
}

const MAX_TILES = 6;

export default function ClubGallery({ images }: Props) {
  const { isOpen, openModal, closeModal } = useModal();
  const [currentIndex, setCurrentIndex] = useState(0);

  // The cover photo is already surfaced by the banner, so the gallery shows only
  // the additional photos. Exclude the exact image the banner uses.
  const cover = getVenueCoverImage(images);
  const galleryImages = images.filter((image) => image.id !== cover?.id);

  const openAt = (index: number) => {
    setCurrentIndex(index);
    openModal();
  };
  const showPrev = () =>
    setCurrentIndex((i) => (i - 1 + galleryImages.length) % galleryImages.length);
  const showNext = () => setCurrentIndex((i) => (i + 1) % galleryImages.length);

  // Mirror MediaGallery's full-screen viewer: lock body scroll and wire keyboard
  // navigation while open.
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    document.body.classList.add('overflow-hidden');
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeModal();
      if (event.key === 'ArrowLeft') showPrev();
      if (event.key === 'ArrowRight') showNext();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.classList.remove('overflow-hidden');
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, closeModal]);

  // Nothing to browse if the cover is the only photo.
  if (galleryImages.length === 0) {
    return null;
  }

  const tiles = galleryImages.slice(0, MAX_TILES);
  const remaining = galleryImages.length - MAX_TILES;

  return (
    <>
      <section className="grid grid-cols-3 gap-2">
        {tiles.map((image, index) => {
          const showMoreOverlay = index === MAX_TILES - 1 && remaining > 0;
          return (
            <div
              key={image.id}
              onClick={() => openAt(index)}
              className="relative aspect-square cursor-pointer overflow-hidden rounded-xl"
            >
              <img src={image.url} alt="" className="h-full w-full object-cover" />
              {showMoreOverlay && (
                <Button
                  variant="primary-light"
                  size="sm"
                  isInline
                  iconLeft={<MoreGallery className="h-4 w-4" />}
                  onClick={(event) => {
                    event.stopPropagation();
                    openAt(index);
                  }}
                  className="absolute bottom-3 left-1/2 -translate-x-1/2 whitespace-nowrap shadow-fab"
                >
                  {remaining} more
                </Button>
              )}
            </div>
          );
        })}
      </section>

      {isOpen && (
        <div className="fixed inset-0 z-[60] flex flex-col bg-color-bg-lightmode-invert dark:bg-color-bg-darkmode-invert">
          <div className="relative flex shrink-0 items-center justify-center px-10 py-8">
            <span className="typography-product-heading text-color-text-lightmode-invert dark:text-color-text-darkmode-invert">
              {currentIndex + 1}/{galleryImages.length}
            </span>
            <button
              type="button"
              onClick={() => closeModal()}
              aria-label="Close gallery"
              className="absolute right-8 top-1/2 -translate-y-1/2"
            >
              <CloseIcon className="h-8 w-8 text-color-text-lightmode-invert dark:text-color-text-darkmode-invert" />
            </button>
          </div>

          <div className="relative flex flex-1 items-center justify-center px-10 pb-10">
            <button
              type="button"
              onClick={showPrev}
              aria-label="Previous photo"
              className="absolute left-[5%] rounded-full border border-color-border-card-lightmode p-3 dark:border-color-border-card-darkmode lg:left-[10%]"
            >
              <GalleryLeft className="h-5 w-5 text-color-text-lightmode-invert dark:text-color-text-darkmode-invert" />
            </button>

            <img
              src={galleryImages[currentIndex].url}
              alt={`Photo ${currentIndex + 1}`}
              className="max-h-[80vh] max-w-full rounded-lg object-contain"
            />

            <button
              type="button"
              onClick={showNext}
              aria-label="Next photo"
              className="absolute right-[5%] rounded-full border border-color-border-card-lightmode p-3 dark:border-color-border-card-darkmode lg:right-[10%]"
            >
              <GalleryRight className="h-5 w-5 text-color-text-lightmode-invert dark:text-color-text-darkmode-invert" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
